import React from 'react';
import manifest from '@/data/image-manifest.json';

/**
 * Responsive <picture> for anything under /images/.
 *
 * Every image in public/images has AVIF + WebP derivatives at several widths,
 * generated by `python tools/optimize-images.py` and described in
 * image-manifest.json. This component turns a plain `/images/foo.jpg` path into
 * the full <picture> — srcset per format, a sized fallback, and the intrinsic
 * width/height that keeps layout from shifting.
 *
 * Unknown paths fall through to a bare <img> so nothing breaks if an image is
 * added without regenerating the manifest.
 *
 * Props beyond the ones destructured below are spread onto the <img>.
 *
 *   sizes    — CSS `sizes` string. Default assumes a full-width image; pass the
 *              real layout width for anything narrower or the browser will
 *              fetch a rung larger than it needs.
 *   priority — set on the LCP image only. Switches to eager loading + high
 *              fetchpriority; everything else lazy-loads.
 *
 * The <picture> wrapper defaults to `display: contents` so it vanishes from
 * layout — callers keep sizing the <img> with the same classes they used before
 * (`w-full h-full`, flex/grid children, etc.) and nothing shifts. Pass
 * pictureClassName to opt out where a real wrapper box is wanted.
 */
export default function Img({
  src,
  alt = '',
  sizes = '100vw',
  priority = false,
  className,
  pictureClassName = 'contents',
  style,
  width,
  height,
  ...rest
}) {
  const entry = manifest[src];

  if (!entry) {
    return (
      <img
        src={src}
        alt={alt}
        className={className}
        style={style}
        width={width}
        height={height}
        loading={priority ? 'eager' : 'lazy'}
        decoding={priority ? 'sync' : 'async'}
        {...rest}
      />
    );
  }

  const srcSet = (pattern) =>
    entry.widths.map((w) => `${pattern.replace('{w}', w)} ${w}w`).join(', ');

  return (
    <picture className={pictureClassName}>
      <source type="image/avif" srcSet={srcSet(entry.avif)} sizes={sizes} />
      <source type="image/webp" srcSet={srcSet(entry.webp)} sizes={sizes} />
      <img
        src={entry.fallback}
        alt={alt}
        className={className}
        style={style}
        width={width ?? entry.width}
        height={height ?? entry.height}
        loading={priority ? 'eager' : 'lazy'}
        decoding={priority ? 'sync' : 'async'}
        fetchPriority={priority ? 'high' : undefined}
        {...rest}
      />
    </picture>
  );
}
